import { TimeUnitKind, TimeUnitOptions } from '../schema';
import { FormatValueOptions, UnitGroupConfig, UnitConfig } from './types';
import { hasDecimalPlaces, limitDecimalPlaces } from './utils';

const TIME_GROUP = 'Time';
export const TIME_GROUP_CONFIG: UnitGroupConfig = {
  label: 'Time',
  decimal_places: true,
};
export const TIME_UNIT_CONFIG: Readonly<Record<TimeUnitKind, UnitConfig>> = {
  Nanoseconds: {
    group: TIME_GROUP,
    label: 'Nanoseconds',
  },
  Microseconds: {
    group: TIME_GROUP,
    label: 'Microseconds',
  },
  Milliseconds: {
    group: TIME_GROUP,
    label: 'Milliseconds',
  },
  Seconds: {
    group: TIME_GROUP,
    label: 'Seconds',
  },
  Minutes: {
    group: TIME_GROUP,
    label: 'Minutes',
  },
  Hours: {
    group: TIME_GROUP,
    label: 'Hours',
  },
  Days: {
    group: TIME_GROUP,
    label: 'Days',
  },
  Weeks: {
    group: TIME_GROUP,
    label: 'Weeks',
  },
  Months: {
    group: TIME_GROUP,
    label: 'Months',
  },
  Years: {
    group: TIME_GROUP,
    label: 'Years',
  },
};

// Mapping of time units to what Intl.NumberFormat formatter expects
// https://v8.dev/features/intl-numberformat#units
export enum PersesTimeToIntlTime {
  Nanoseconds = 'nanosecond',
  Microseconds = 'microsecond',
  Milliseconds = 'millisecond',
  Seconds = 'second',
  Minutes = 'minute',
  Hours = 'hour',
  Days = 'day',
  Weeks = 'week',
  Months = 'month',
  Years = 'year',
}

/**
 * Note: This conversion will not be exactly accurate for months and years,
 * due variations in the lengths of months (i.e. 28 - 31 days) and years (i.e. leap years).
 * For precision with months and years, we would need more complex algorithms and/or external libraries.
 * However, we expect that measurements in months and years will be rare.
 */
const TIME_UNIT_IN_SECONDS: Record<TimeUnitKind, number> = {
  Years: 31536000, // 365 days
  Months: 2592000, // 30 days
  Weeks: 604800,
  Days: 86400,
  Hours: 3600,
  Minutes: 60,
  Seconds: 1,
  Milliseconds: 0.001,
  Microseconds: 0.000001,
  Nanoseconds: 0.000000001,
};

/**
 * Avoid floating point errors that occur when dividing by small values.
 * Instead, multiply by large value.
 *
 * In future, we may want to consider Big.js or Decimal.js to be even safer.
 */
const TIME_UNIT_IN_SECONDS_MULTIPLIER = {
  Milliseconds: 1000,
  Microseconds: 1000000,
  Nanoseconds: 1000000000,
};

const LARGEST_TO_SMALLEST_TIME_UNITS: TimeUnitKind[] = [
  'Years',
  'Months',
  'Weeks',
  'Days',
  'Hours',
  'Minutes',
  'Seconds',
  'Milliseconds',
  'Microseconds',
  'Nanoseconds',
];

/**
 * Choose the largest time unit that still produces a number greater than 1.
 */
export function getBestTimeUnit(value: number, kind: TimeUnitKind): TimeUnitKind {
  // Initialize to please Typescript
  let bestTimeUnit: TimeUnitKind = LARGEST_TO_SMALLEST_TIME_UNITS[0] || 'Years';
  let valueInBestTimeUnit: number = getValueInTimeUnit(value, kind, bestTimeUnit);

  // Start with largest time unit ('Years').
  // If this produces a number greater than zero, this is best time unit.
  // If not, proceed to next largest time unit and repeat.
  // If nothing produces a number greater than zero, we settle for the smallest time unit ('Nanoseconds').
  for (const timeUnit of LARGEST_TO_SMALLEST_TIME_UNITS) {
    bestTimeUnit = timeUnit as TimeUnitKind;
    valueInBestTimeUnit = getValueInTimeUnit(value, kind, bestTimeUnit);
    // Use absolute value to handle negative numbers
    if (Math.abs(valueInBestTimeUnit) >= 1) {
      break;
    }
  }

  return bestTimeUnit;
}

export function getValueInTimeUnit(value: number, originalUnit: TimeUnitKind, targetUnit: TimeUnitKind): number {
  if (targetUnit === 'Milliseconds' || targetUnit === 'Microseconds' || targetUnit === 'Nanoseconds') {
    return value * TIME_UNIT_IN_SECONDS[originalUnit] * TIME_UNIT_IN_SECONDS_MULTIPLIER[targetUnit];
  } else {
    return (value * TIME_UNIT_IN_SECONDS[originalUnit]) / TIME_UNIT_IN_SECONDS[targetUnit];
  }
}

function isMonthOrYear(kind: TimeUnitKind): boolean {
  return kind === 'Months' || kind === 'Years';
}

type FormatTimeOptions = TimeUnitOptions & Pick<FormatValueOptions, 'trimZeros'>;

export function formatTime(value: number, { kind, decimal_places, trimZeros = true }: FormatTimeOptions): string {
  if (value === 0) return '0s';

  const bestTimeUnit = getBestTimeUnit(value, kind);
  const valueInBestTimeUnit = getValueInTimeUnit(value, kind, bestTimeUnit);

  const formatterOptions: Intl.NumberFormatOptions = {
    style: 'unit',
    unit: PersesTimeToIntlTime[bestTimeUnit],
    unitDisplay: isMonthOrYear(bestTimeUnit) ? 'long' : 'narrow',
  };

  if (hasDecimalPlaces(decimal_places)) {
    formatterOptions.maximumFractionDigits = limitDecimalPlaces(decimal_places);
    if (!trimZeros) {
      formatterOptions.minimumFractionDigits = limitDecimalPlaces(decimal_places);
    }
  } else {
    if (Math.abs(valueInBestTimeUnit) < 1) {
      // For values < 1, allow up to 3 significant digits
      formatterOptions.maximumSignificantDigits = 3;
    } else {
      // For values >= 1, allow up to 2 decimal places.
      formatterOptions.maximumFractionDigits = 2;
    }
  }

  const formatter = Intl.NumberFormat('en-US', formatterOptions);
  return formatter.format(valueInBestTimeUnit);
}
